import axiosInstance from '../services/axiosInstance';
import type {ICancelEventBody} from '../types/dto/ICancelEventBody';
import type {ICreateEventBody} from '../types/dto/ICreateEventBody';
import type {IGetAvailabilityQuery} from '../types/dto/IGetAvailabilityQuery';
import type {IGetAvailabilityRangeQuery} from '../types/dto/IGetAvailabilityRangeQuery';
import type {IGetAvailabilityRangeResponse} from '../types/dto/IGetAvailabilityRangeResponse';
import type {INoAvailabilityBody} from '../types/dto/INoAvailabilityBody';
import type {IRescheduleRequestBody} from '../types/dto/IRescheduleRequestBody';
import type {IRsvpUpdateStatusBody} from '../types/dto/IRsvpUpdateStatusBody';
import type {ISendLinkBody} from '../types/dto/ISendLinkBody';
import type {IUpdateEventBody} from '../types/dto/IUpdateEventBody';
import type {IEvent} from '../types/IEvent';
import type {IEventTemplate} from '../types/IEventTemplate';
import type {ISession} from '../types/ISession';
import type {ITimeSlots} from '../types/ITimeSlots';
import type {IReasons} from '../types/IReasons';
import type {IRequestMoreAvailabilityBody} from '../types/dto/IRequestMoreAvailabilityBody';


export const getSession = (sessionId: string): Promise<ISession> => {
  return axiosInstance.get(`/v1/scheduler/session-link/${sessionId}`);
};

export const getEventTemplate = async (
  agentId: string,
  urlSuffix?: string,
): Promise<IEventTemplate> => {
  const url = urlSuffix
    ? `/v1/scheduler/event-template/by-url-suffix/${agentId}/${urlSuffix}`
    : `/v1/scheduler/event-template/by-url-suffix/${agentId}`;

  return axiosInstance.get(url);
};

export const getAvailabilityRange = async (
  templateId: string,
  query: IGetAvailabilityRangeQuery,
): Promise<IGetAvailabilityRangeResponse> => {
  return axiosInstance.get(
    `/v1/scheduler/event-template/${templateId}/availability-range`,
    {
      params: query,
    },
  );
};

export const getAvailability = async (
  templateId: string,
  query: IGetAvailabilityQuery,
): Promise<ITimeSlots> =>
  axiosInstance.get(`/v1/scheduler/event-template/${templateId}/availability`, {
    params: query,
  });

export const getEvent = async (eventId: string): Promise<IEvent> =>
  axiosInstance.get(`/v1/scheduler/event/${eventId}`);

export const sendLink = async (eventId: string, body: ISendLinkBody) =>
  axiosInstance.post(`/v1/scheduler/event/${eventId}/send-link`, body);

export const rescheduleRequest = (
  eventId: string,
  body: IRescheduleRequestBody,
) =>
  axiosInstance.post(`/v1/scheduler/event/${eventId}/reschedule-request`, body);

export const createEvent = async (
  body: ICreateEventBody,
): Promise<IEvent | IEvent[]> => {
  return axiosInstance.post(`/v1/scheduler/events`, body);
};

export const createMoreAvailabilityEvent = async (
  body: IRequestMoreAvailabilityBody,
) => {
  return axiosInstance.post(`/v1/scheduler/session-links/slot-request`, body);
};

export const cancelEvent = (eventId: string, body: ICancelEventBody) =>
  axiosInstance.delete(`/v1/scheduler/event/${eventId}`, {
    data: body,
  });

export const updateEvent = (
  eventId: string,
  body: IUpdateEventBody,
): Promise<IEvent> => axiosInstance.put(`/v1/scheduler/event/${eventId}`, body);

export const rsvpUpdateStatus = (
  eventId: string,
  aid: string,
  body: IRsvpUpdateStatusBody,
) =>
  axiosInstance.post(
    `/v1/scheduler/events/${eventId}/participants/${aid}/rsvp`,
    body,
  );

export const saveToCalender = (eventId: string) =>
  axiosInstance.post(`/v1/scheduler/events/${eventId}/ics-file`);

export const noAvailability = (
  templateId: string,
  body: INoAvailabilityBody,
) => {
  return axiosInstance.post(
    `/v1/scheduler/event-template/${templateId}/no-availability`,
    body,
  );
};

export const getReasons = async (): Promise<IReasons> =>
  axiosInstance.get(`/v1/scheduler/event-action-reasons`);
